import { memo, useCallback, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useTheme } from '@mui/material/styles';

export interface DocumentSearchProps {
  open: boolean;
  query: string;
  matchCount: number;
  activeIndex: number;
  onQueryChange: (query: string) => void;
  onActiveIndexChange: (index: number) => void;
  onClose: () => void;
}

/**
 * In-document find bar.
 *
 * Highlighting itself happens in the render pipeline; this component owns the
 * query, the active match cursor and the keyboard contract (Enter / Shift+Enter
 * to step through results, Escape to dismiss).
 */
export const DocumentSearch = memo(function DocumentSearch({
  open,
  query,
  matchCount,
  activeIndex,
  onQueryChange,
  onActiveIndexChange,
  onClose,
}: DocumentSearchProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const step = useCallback(
    (delta: number) => {
      if (matchCount === 0) return;
      onActiveIndexChange((activeIndex + delta + matchCount) % matchCount);
    },
    [activeIndex, matchCount, onActiveIndexChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        step(event.shiftKey ? -1 : 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
    [step, onClose],
  );

  const hasQuery = query.trim() !== '';

  return (
    <Collapse in={open} unmountOnExit>
      <Paper
        variant="outlined"
        role="search"
        className="search"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderRadius: 2,
          backgroundColor: theme.markdown.glass,
        }}
      >
        <TextField
          inputRef={inputRef}
          size="small"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Find in document"
          inputProps={{ 'aria-label': 'Find in document' }}
          sx={{ flex: 1, minWidth: 140 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Typography
          variant="caption"
          role="status"
          aria-live="polite"
          sx={{
            minWidth: 76,
            textAlign: 'center',
            color: hasQuery && matchCount === 0 ? 'error.main' : 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {!hasQuery ? '' : matchCount === 0 ? 'No results' : `${activeIndex + 1} of ${matchCount}`}
        </Typography>

        <Tooltip title="Previous match (Shift+Enter)">
          <span>
            <IconButton size="small" aria-label="Previous match" disabled={matchCount === 0} onClick={() => step(-1)}>
              <KeyboardArrowUpRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next match (Enter)">
          <span>
            <IconButton size="small" aria-label="Next match" disabled={matchCount === 0} onClick={() => step(1)}>
              <KeyboardArrowDownRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ width: 1, height: 24, backgroundColor: theme.palette.divider, mx: 0.5 }} />

        <Tooltip title="Close search (Esc)">
          <IconButton size="small" aria-label="Close search" onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </Collapse>
  );
});

export default DocumentSearch;
